import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useWindowDimensions } from 'react-native';
import { FileText, ArrowLeft } from 'lucide-react';
import ListsManager from '../services/ListsManager';
import DailyReportView, { REPORT_STYLES, formatDate } from '../components/DailyReportView';
import { clearReportBootScreen } from '../config';

// Standalone, login-free daily reports. Lives at reports.reactfirst.ai/r/:token
// (and /r/:token on any host). The token grants read access to the org's whole
// sent-report history: a sidebar lists every day, and clicking one loads it — all
// with NO auth, no nav, no path into the system.
const PublicReportPage = () => {
  const { token } = useParams();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [reports, setReports] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // On phones the sidebar and the report are two screens. The emailed link says "view
  // your report", so it must LAND on the report — not on a list of dates the reader then
  // has to tap through. The back button reveals the day list.
  const [showDetail, setShowDetail] = useState(true);

  // Initial load: org name + report list + the emailed (default) report.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await ListsManager.getSharedReport(token);
      if (cancelled) return;
      if (res.ok && res.data) {
        setOrgName(res.data.org_name || '');
        setReports(res.data.reports || []);
        setSelectedDate(res.data.report_date || null);
        setReportData({
          org_name: res.data.org_name,
          report_date: res.data.report_date,
          report_data: res.data.report_data,
        });
        setLoading(false);
      } else {
        setNotFound(true);
        setLoading(false);
      }
      // The report (or the not-found message) is now on screen — drop the boot screen
      // that was covering the prerendered marketing landing.
      clearReportBootScreen();
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const handleSelectReport = async (date) => {
    if (date === selectedDate && reportData) {
      if (isMobile) setShowDetail(true);
      return;
    }
    setSelectedDate(date);
    setReportLoading(true);
    setReportData(null);
    if (isMobile) setShowDetail(true);
    const res = await ListsManager.getSharedReportDay(token, date);
    if (res.ok && res.data) {
      setReportData({
        org_name: res.data.org_name,
        report_date: res.data.report_date,
        report_data: res.data.report_data,
      });
    } else {
      setReportData(null);
    }
    setReportLoading(false);
  };

  if (loading) {
    return (
      <div className="rp-page">
        <style>{REPORT_STYLES}</style>
        <div className="rp-detail rp-detail--empty">
          <div className="rp-spinner" />
          <div className="rp-detail__empty-text">טוען דו״ח...</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="rp-page">
        <style>{REPORT_STYLES}</style>
        <div className="rp-detail rp-detail--empty">
          <div className="rp-detail__empty-text" dir="rtl">הקישור אינו תקין או שפג תוקפו</div>
        </div>
      </div>
    );
  }

  const sidebarJsx = (
    <div className="rp-sidebar" style={{ width: isMobile ? '100%' : 280, minWidth: isMobile ? undefined : 280 }}>
      <div className="rp-sidebar__header">
        <h1 className="rp-sidebar__title">דו״חות יומיים</h1>
        <div className="rp-sidebar__subtitle">{orgName}</div>
      </div>
      <div className="rp-sidebar__list">
        {reports.length === 0 ? (
          <div className="rp-sidebar__empty">
            <FileText size={36} color="#94a3b8" />
            <span>אין דו״חות עדיין</span>
          </div>
        ) : (
          reports.map(report => (
            <div
              key={report.report_date}
              className={`rp-report-item ${selectedDate === report.report_date ? 'rp-report-item--selected' : ''}`}
              onClick={() => handleSelectReport(report.report_date)}
            >
              <div className="rp-report-item__date">{formatDate(report.report_date)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const detailJsx = reportLoading ? (
    <div className="rp-detail rp-detail--empty">
      <div className="rp-spinner" />
      <div className="rp-detail__empty-text">טוען דו״ח...</div>
    </div>
  ) : reportData ? (
    <DailyReportView
      reportData={reportData}
      isMobile={isMobile}
      onBack={() => setShowDetail(false)}
    />
  ) : (
    <div className="rp-detail rp-detail--empty">
      {isMobile && (
        <button className="rp-detail__back" onClick={() => setShowDetail(false)}>
          <ArrowLeft size={16} />
          <span>חזרה לרשימה</span>
        </button>
      )}
      <div className="rp-detail__empty-text">הדו״ח לא נמצא</div>
    </div>
  );

  return (
    <div className="rp-page">
      <style>{REPORT_STYLES}</style>
      {isMobile ? (
        showDetail ? detailJsx : sidebarJsx
      ) : (
        <>
          {sidebarJsx}
          <div className="rp-divider rp-divider--static" />
          {detailJsx}
        </>
      )}
    </div>
  );
};

export default PublicReportPage;
